import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import { readSnagcartSettings } from '../../hooks/useSnagcartSettings';

/**
 * VoiceInput — small mic button that uses the Web Speech API to dictate
 * into the surrounding chat composer. Hidden unless `beta.voice_input`
 * is enabled in the user's settings. Falls back gracefully when the
 * browser doesn't support SpeechRecognition (Safari iOS etc.).
 *
 * Props:
 *   onTranscript(text)  — called as the user speaks (interim) and finalises.
 *   disabled            — match the rest of the composer (e.g. while sending).
 *   compact             — if true, render a small icon-only button (~h-[60px]).
 */
export default function VoiceInput({ onTranscript, disabled, compact }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recRef = useRef(null);
  const settings = readSnagcartSettings();
  const enabled = !!settings?.beta?.voice_input;

  useEffect(() => {
    const SR = (typeof window !== 'undefined') && (window.SpeechRecognition || window.webkitSpeechRecognition);
    setSupported(!!SR);
  }, []);

  if (!enabled) return null;

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice input is not supported in this browser. Chrome on desktop works best.');
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    let finalBuffer = '';
    rec.onresult = (ev) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const txt = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalBuffer += txt + ' ';
        else interim += txt;
      }
      onTranscript((finalBuffer + interim).trim(), { interim: !ev.results[ev.results.length - 1].isFinal });
    };
    rec.onerror = (ev) => {
      toast.error(`Voice input error: ${ev.error || 'unknown'}`);
      setListening(false);
    };
    rec.onend = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
    toast.message('Listening — speak naturally, click again to stop.');
  };

  const stop = () => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* ignore */ }
      recRef.current = null;
    }
    setListening(false);
  };

  const sizeCls = compact ? 'h-[60px] w-[44px]' : 'h-9 w-9';

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      disabled={disabled || !supported}
      data-testid="voice-input-btn"
      title={
        !supported ? 'Voice input not supported in this browser'
          : listening ? 'Stop dictation'
          : 'Start dictation (Web Speech API)'
      }
      className={`${sizeCls} rounded-lg flex items-center justify-center transition-colors ${
        listening
          ? 'bg-red-500/20 text-red-300 border border-red-500/30 ring-2 ring-red-500/30 animate-pulse'
          : 'bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300 border border-transparent'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}
